import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * A card that grows into a full view, and shrinks back into the card
 * -------------------------------------------------------------
 * Asked for on the Usage page (Huanrui, 2026-10-01): a person's card, or a
 * month's bill, opens like Amazon's View details -- but the card itself is
 * what grows. The panel starts exactly where the card is, at its size and
 * with its corners, and travels out to fill the screen; what the card said
 * (`face`) fades as the charts (`children`) fade in. Closing runs the same
 * thing backwards, to wherever the card is now (the page may have scrolled).
 *
 * Position and size are animated rather than a scale, so nothing inside is
 * stretched on the way. The card stays hidden while its panel is open, so it
 * reads as one thing lifting off the page and settling back.
 *
 * Esc, the x and a click outside all close it. Without motion (the system
 * setting), it simply appears and disappears.
 */

const OPEN_MS = 420
const CLOSE_MS = 340
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

interface Box {
  left: number
  top: number
  width: number
  height: number
}

function boxOf(element: Element | null): Box | null {
  if (!element) return null
  const r = element.getBoundingClientRect()
  return { left: r.left, top: r.top, width: r.width, height: r.height }
}

/** Where the panel ends up: most of the window, centred. */
function finalBox(): Box {
  const width = Math.min(1240, window.innerWidth * 0.92)
  const height = Math.min(880, window.innerHeight * 0.88)
  return { left: (window.innerWidth - width) / 2, top: (window.innerHeight - height) / 2, width, height }
}

const px = (box: Box) => ({
  left: `${box.left}px`,
  top: `${box.top}px`,
  width: `${box.width}px`,
  height: `${box.height}px`,
})

export function Morph({
  source,
  face,
  label,
  onClosed,
  children,
}: {
  /** The card it grows out of and returns to. */
  source: () => HTMLElement | null
  /** What the card shows, carried across the change. */
  face: ReactNode
  label: string
  /** Called once the panel is back in the card and gone. */
  onClosed: () => void
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement | null>(null)
  const veil = useRef<HTMLDivElement | null>(null)
  const faceRef = useRef<HTMLDivElement | null>(null)
  const body = useRef<HTMLDivElement | null>(null)
  const closing = useRef(false)
  const [settled, setSettled] = useState(false)
  const still = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  useLayoutEffect(() => {
    const to = finalBox()
    const from = boxOf(source()) ?? to
    const node = panel.current
    if (!node) return
    Object.assign(node.style, px(to))
    if (still) {
      setSettled(true)
      return
    }
    const opts = { duration: OPEN_MS, easing: EASE, fill: 'both' as const }
    node.animate(
      [
        { ...px(from), borderRadius: '14px' },
        { ...px(to), borderRadius: '18px' },
      ],
      opts,
    ).onfinish = () => setSettled(true)
    veil.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: OPEN_MS, easing: EASE, fill: 'both' })
    faceRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: OPEN_MS * 0.45, easing: 'ease-out', fill: 'both' })
    body.current?.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: OPEN_MS * 0.55,
      delay: OPEN_MS * 0.45,
      easing: 'ease-out',
      fill: 'both',
    })
    // Measured once, on purpose: the source is read again on the way back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const close = useCallback(() => {
    if (closing.current) return
    closing.current = true
    const node = panel.current
    const from = boxOf(node) ?? finalBox()
    const to = boxOf(source())
    if (still || !node || !to) {
      onClosed()
      return
    }
    setSettled(false)
    body.current?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: CLOSE_MS * 0.35, easing: 'ease-in', fill: 'both' })
    faceRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: CLOSE_MS * 0.5,
      delay: CLOSE_MS * 0.5,
      easing: 'ease-out',
      fill: 'both',
    })
    veil.current?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: CLOSE_MS, easing: EASE, fill: 'both' })
    node.animate(
      [
        { ...px(from), borderRadius: '18px' },
        { ...px(to), borderRadius: '14px' },
      ],
      { duration: CLOSE_MS, easing: EASE, fill: 'both' },
    ).onfinish = () => onClosed()
  }, [onClosed, source, still])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close])

  return (
    <div className="morph" role="dialog" aria-modal="true" aria-label={label}>
      <div ref={veil} className="morph__veil" onClick={close} />
      <div ref={panel} className="morph__panel">
        <div ref={faceRef} className="morph__face" aria-hidden="true">
          {face}
        </div>
        <div ref={body} className="morph__body">
          {children}
        </div>
        <button
          type="button"
          className="morph__close"
          aria-label="Close"
          title="Close"
          onClick={close}
          style={{ opacity: settled ? 1 : 0 }}
        >
          {/* Drawn, not the × character: a glyph sits on the font's baseline
              and never quite in the middle of the circle. */}
          <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false">
            <path d="M1.5 1.5l9 9M10.5 1.5l-9 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  )
}
